import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";


function Economics27() {
     const contentRef = useRef(null);
       const links = {
      prev: "/economics-26", 
      index: "/index-economics",                 
      next: "/economics-28", 
    };
      const titleText ="The Broken Window Fallacy";

      const questions = [
  {
    text: "1. What does the broken window fallacy illustrate?",
    options: [
      { value: "A", label: "A. That destruction creates new wealth in the economy" },
      { value: "B", label: "B. That visible economic activity always indicates prosperity" },
      {
        value: "C",
        label:
          "C. That repairing damage only restores what was lost and does not create new wealth",
      },
      { value: "D", label: "D. That government spending always boosts economic growth" },
    ],
    correct: "C",
  },
  {
    text: "2. What is the “unseen” cost in the broken window fallacy?",
    options: [
      { value: "A", label: "A. The cost of hiring the glazier" },
      {
        value: "B",
        label:
          "B. The loss of alternative uses for money spent on repairs",
      },
      { value: "C", label: "C. The increase in public works jobs" },
      { value: "D", label: "D. The visible benefits to local businesses" },
    ],
    correct: "B",
  },
  {
    text: "3. How does the fallacy relate to capital in economics?",
    options: [
      { value: "A", label: "A. Destruction of capital leads to faster economic growth" },
      {
        value: "B",
        label:
          "B. Preserving capital is less important than spending money quickly",
      },
      {
        value: "C",
        label:
          "C. Destroyed capital must be replaced before new wealth can be created",
      },
      { value: "D", label: "D. Capital goods are not relevant to economic productivity" },
    ],
    correct: "C",
  },
  {
    text: "4. Why do some government policies fall into the broken window fallacy trap?",
    options: [
      { value: "A", label: "A. Because they focus only on long-term consequences" },
      {
        value: "B",
        label:
          "B. Because they prioritize voluntary exchange over political decision",
      },
      {
        value: "C",
        label:
          "C. Because they emphasize immediate visible results without considering opportunity costs",
      },
      { value: "D", label: "D. Because they always increase overall productivity" },
    ],
    correct: "C",
  },
];

 
  // State to track selected answers for all questions
      const [answers, setAnswers] = useState({});
    
      // Function to update an answer
      const handleAnswerChange = (questionIndex, selectedValue) => {
        setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
      };
    
      // Compute score
      const score = questions.reduce((total, q, index) => {
        return total + (answers[index] === q.correct ? 1 : 0);
      }, 0);
    

  return (
    <div className="container">
      <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={27}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />
      <p className="content" ref={contentRef}>
        
        Welcome to <b>Day 27</b>! Today, we explore a common but important
        economic misunderstanding known as the broken window fallacy. This
        lesson will help you see why destruction, whether caused by accidents or
        disasters, does not create true wealth or economic progress. By learning
        to distinguish between what is immediately visible and what is hidden in
        economic decisions, you will gain insight into opportunity costs,
        capital preservation, and the long-term consequences of policies. This
        understanding is crucial for thinking clearly about how economies grow
        and why protecting productive resources matters for a prosperous
        society.
        <br /> One of the most enduring economic fallacies is the belief that
        destruction and spending caused by disasters can stimulate economic
        activity and growth. This idea, often referred to as the broken window
        fallacy, is best illustrated through the simple tale of a shopkeeper
        whose storefront window is shattered by a careless youth. Onlookers may
        console the shopkeeper by suggesting that at least the glazier will
        benefit from the repair job. The money spent to replace the window will
        circulate through the economy, supporting the glazier and perhaps even
        more workers down the chain. This line of reasoning claims that such
        destruction ultimately benefits society by stimulating economic
        activity.
        <br />
        However, this view misses a critical point. The money spent on repairing
        the broken window is money that could have been spent elsewhere, perhaps
        on a new suit, a book, or investing in a new business venture. The
        fallacy lies in seeing only what is immediately visible, the job given
        to the glazier, while ignoring the unseen opportunities that have been
        lost. This unseen cost is known in economics as the opportunity cost. By
        repairing a broken window, we merely restore what was lost; we do not
        advance society in any meaningful way. Resources are diverted from
        productive uses to restore what once existed.
        <br />
        <br />
        <b>What Is Seen and What Is Not Seen</b>
        <br />
        <br />
        To fully understand the broken window fallacy, one must grasp the
        distinction between what is seen and what is not seen. The glazier
        receives payment and the window is fixed. This is seen. What is not seen
        is the tailor who was not hired, the goods that were not purchased, or
        the entrepreneur who never received investment. The fallacy arises from
        focusing on the immediate, visible consequences of an action while
        ignoring the indirect, longer-term, and less obvious effects.
        <br />
        The error is not merely a theoretical one; it manifests in real-world
        policy decisions. Governments may propose public works projects as
        economic stimulants after natural disasters or during recessions. Such
        actions are often defended as necessary to create jobs or revitalize
        infrastructure. But the key question remains: what alternative uses
        could those resources have been put to if not absorbed by government
        spending or disaster recovery? When resources are allocated by political
        decision rather than voluntary exchange, they are often misallocated,
        leading to inefficiencies and distortions.
        <br />
        <br />
        <b>The Misconception of Productive Destruction</b>
        <br />
        <br />A common extension of the broken window fallacy is the belief that
        large-scale destruction, such as war or natural disasters, can boost
        economic activity by creating a demand for reconstruction. This idea,
        sometimes expressed in the phrase "war is good for the economy," ignores
        the vast human and material costs involved. Destroying capital goods,
        infrastructure, and human lives does not create prosperity. It diverts
        energy and resources away from building new wealth to merely replacing
        what has been lost.
        <br />
        The concept of productive destruction confuses economic activity with
        economic benefit. It is true that rebuilding after a war requires labor
        and materials. But these efforts are not a net gain. They are a cost.
        The resources consumed in reconstruction could have been used to improve
        lives and increase prosperity had the destruction never occurred.
        Economic well-being is measured not by the volume of activity, but by
        the improvement in people’s standard of living.
        <br />
        <br />
        <b>Demand versus Need</b>
        <br />
        <br />
        One important implication of this fallacy is the need to distinguish
        between demand and need. A broken window creates a need for repair but
        not an economically productive demand. Demand in economics is not simply
        a desire for goods or services. It is desire backed by purchasing power.
        Needs are limitless. Humans may need food, shelter, safety, education,
        and health. But not every need constitutes economic demand.
        <br />
        <br />
        Resources flow toward demand, not need. This distinction has important
        consequences. A society cannot organize its economy merely around needs;
        it must reckon with the reality of scarcity. The mere existence of need
        does not justify allocating resources to address it unless there is a
        corresponding demand that reflects individuals’ willingness and ability
        to trade. When policies are crafted to fulfill needs without regard to
        economic demand or opportunity cost, the result is often inefficiency,
        waste, and unintended consequences.
        <br />
        <br />
        <b> Rebuilding and the Role of Capital</b>
        <br />
        <br />
        The fallacy also illustrates the critical role of capital in economic
        development. Capital goods, such as tools, machines, buildings, and
        infrastructure, represent accumulated savings and productive capacity.
        When capital is destroyed, whether by war, disaster, or neglect, society
        must divert effort to replace it before new capital can be created.
        Productivity and wealth arise not from destruction, but from the
        accumulation of capital and the deployment of resources in ways that
        serve future-oriented goals. This requires saving, planning, and the
        free flow of prices to allocate resources efficiently. When capital is
        consumed in restoration, the potential for future growth is delayed or
        diminished. In this sense, the broken window is a metaphor for the
        fragility of capital and the importance of protecting and cultivating
        it.
        <br />
        <br />
        <b>Fallacies in Policy and Popular Opinion</b>
        <br />
        <br />
        Despite its clarity, the broken window fallacy persists because it
        aligns with superficial thinking and populist sentiment. People want to
        see results: jobs created, businesses busy, streets bustling with
        activity. But economic progress cannot be evaluated solely by visible
        metrics. True economic understanding requires thinking through the full
        chain of consequences and recognizing the opportunity costs of every
        decision.
        <br />
        This fallacy is embedded in many policies that seek to create employment
        through public spending or to redistribute resources by force. While
        these policies may produce immediate and visible results, they often
        conceal deeper inefficiencies. By taking resources from one group to
        benefit another, they reduce overall productivity and create perverse
        incentives. The illusion of benefit comes at the expense of unseen
        losses.
        <br />
        <br />
        <b>The Fallacy in Modern Context</b>
        <br />
        <br />
        In our present world, the broken window fallacy takes many forms.
        Natural disasters are sometimes described as economic opportunities.
        Public infrastructure projects are launched as job-creation schemes.
        Insurance payouts are treated as windfalls. But the core logic remains
        flawed. Destruction consumes resources; it does not generate wealth.
        Transfers of money do not create new capital; they merely rearrange
        existing wealth
        <br />
        True economic growth emerges not from activity but from productivity. It
        requires an environment where individuals are free to act, plan, and
        save; where prices reflect real costs and preferences; and where
        resources are guided by voluntary exchange. The broken window story
        teaches us that good economics looks beyond the surface to consider the
        full consequences of action. It is a lesson in humility, patience, and
        long-term thinking.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        In today’s lesson, we examined the broken window fallacy, a mistaken
        belief that destruction can stimulate economic growth. We learned that
        while repair and rebuilding may generate visible economic activity, they
        come at the cost of foregone opportunities elsewhere in the economy. The
        lesson emphasized the importance of recognizing both what is seen and
        what is not seen when evaluating economic actions. We also explored the
        critical role of capital and how its destruction delays future growth.
        Ultimately, the lesson teaches us to think beyond immediate effects and
        consider the long-term consequences of economic decisions for true
        prosperity. <br />
        <br />
        <b>Coming Up next</b>
        <br />
        <br />
        Having uncovered the flaws in the broken window fallacy and the
        importance of looking beyond immediate effects, we now turn to a deeper
        question at the heart of all economic decisions: what gives something
        value? In Day 28, we will explore the Austrian insight that value is not
        objective or inherent, but entirely subjective and is shaped by
        individual preferences, context, and time. This perspective challenges
        many common assumptions and lays the groundwork for understanding how
        prices form, why people trade, and how markets coordinate human action
        in a world of scarce resources.
      </p>
      <h2>Test your understanding</h2>
         {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics27;
